import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { themeVar as colors } from "../design-tokens";
import { BuildProgress } from "./BuildProgress";
import { ReportEditor, type ReportEditorHandle } from "./ReportEditor";
import { ReportProof } from "./ReportProof";
import { measure } from "../ui/measures";
import { StaleReportNotice } from "./EditImpact";
import { downloadUrl, getJobResult, setJobLayout, setSubjectPhoto, type LayoutOption } from "./api";
import { AreaFactsSection } from "./AreaFactsSection";
import { LayoutPicker } from "./LayoutPicker";
import { OverrideTrail } from "./OverrideTrail";
import { PhotoSourceNote } from "./PhotoSourceNote";
import { SubjectPhotoUpload } from "./SubjectPhotoUpload";
import { SubjectPhotoPicker } from "./SubjectPhotoPicker";
import { photoKey, photoOptionToDataUri, type SubjectPhotoOptions } from "./subjectPhotos";
import { PERSONAS, type PersonaId } from "./constants";
import type { AmbJobPublic, AmbReportData } from "./types";
import { DOCUMENT_EDITOR_ENABLED } from "../flags";
import { inertHtml } from "../security/inertHtml";

/* THE DOCUMENT EDITOR, LAZILY, AND THE LAZINESS IS THE REQUIREMENT (S3-02, 2026-09-05).
   `editor/DocumentEditorPanel` pulls the six ProseMirror packages measured at 83.0 KiB gzip on
   their own (`docs/runs/evidence/w2-editor/bundle-delta.md`). A static import here would put every
   byte of that into this screen's chunk for every agent who ever finishes a brief, including the
   ones who never open the document view — which is all of them while the flag is off. `App.tsx`
   takes `./crm` and `editor/EditorScreen` the same way and for the same reason. */
const DocumentEditorPanel = lazy(() =>
  import("../editor/DocumentEditorPanel").then((m) => ({ default: m.DocumentEditorPanel })),
);

/* HOW THE REPORT IS SHOWN. `working` and `full` are unchanged. `document` is S3-02's way in: the
   document editor, on this screen, at this URL — not a second surface reachable only by typing an
   address. */
export type ReportView = "working" | "full" | "document";

/** Every value the view can take, and the only place a string becomes one. */
export const REPORT_VIEWS: readonly ReportView[] = ["working", "full", "document"];

/**
 * A view name from anywhere — a URL, a prop, a bookmark somebody kept — as a view this screen will
 * actually show.
 *
 * THE FLAG IS ENFORCED HERE AND NOWHERE ELSE, and that is the point. `?view=document` is a string
 * a person can type, so "the flag is off" has to mean the screen cannot be put into that state
 * rather than that no button offers it. One function, one rule: with the flag off there are two
 * views and `document` is not one of them, so a deep link to it lands on the working view — the
 * same page an agent with no link at all sees. That equality is what `flag-off parity` means and
 * it is asserted by test.
 */
export function resolveView(raw: string | null | undefined): ReportView {
  if (raw === "full") return "full";
  if (raw === "document" && DOCUMENT_EDITOR_ENABLED) return "document";
  return "working";
}

// UI-050 part 2: the stage vocabulary moved to `cma/constants.ts` so the extract progress panel and
// this build panel cannot drift into two different labels for the same server token. UI-100's rule
// still holds there: every stage the server can emit has a human label, and the fallback is a
// neutral human phrase — never the raw token.

/** Renders a server-supplied HTML fragment as HTML, not escaped text.
 *
 * THE PREVIOUS VERSION OF THIS COMMENT WAS FALSE, and it is why this sink read as reviewed for
 * months. It claimed these strings were "gated by `check_narrative.py`'s allow-list before they
 * ever reach a job's stored `data_json`". `check_narrative.py` checks whether a FIGURE is
 * supported by the comparables. It has never had anything to say about markup, and nothing on
 * this path escaped or filtered the model's output. The strings are not raw user input, but they
 * are model output derived from an uploaded PDF, which is close enough to matter.
 *
 * What makes this safe now is `server/narrative_html.py` (4b, 2026-08-15): every prose field is
 * sanitised where it ENTERS the payload, to an allow-list of `b i em strong br` with no
 * attributes, so the same inert strings reach the PDF, the DOCX and this component. The response
 * also carries an ENFORCED `Content-Security-Policy` now rather than a report-only one.
 *
 * If you widen what this renders, widen the sanitiser's allow-list -- not this comment. */
/* SA-06 (2026-10-10, docs/security/AUDIT-2026-10.md): THE SINK NOW DEFENDS ITSELF. `your_home` rows
 * are text read off an uploaded document and were never escaped on the way in, so the claim above
 * that "every other field is escaped at its sink" was false for them. They cannot be escaped at the
 * source either: the PDF and Word renderers escape at THEIR sinks (`tests/test_escaping_once.py`),
 * so an already-escaped address would print `&lt;` in the deliverable. `inertHtml` therefore keeps
 * the engine's own allow-listed tags and character references and turns any other bracket into
 * text, here, at the last step. */
function Html({ html, style }: { html: string; style?: React.CSSProperties }) {
  return <div style={style} dangerouslySetInnerHTML={{ __html: inertHtml(html) }} />;
}

/** The same thing inline, for a value that sits inside a flex row rather than owning a block.
 *
 * WHY THIS HAD TO EXIST (2026-08-04). `data.your_home` is built by `amb_build`'s `_sf` / `_txt` /
 * `_gar` helpers, which write HTML ENTITIES for the blank case — `"&mdash;"`, not "—" — because
 * their first consumer is `engine/amb_render`, which drops them into HTML for the PDF and calls
 * `html.unescape()` for the DOCX. Both of those render an em dash. This screen printed the value
 * as React TEXT, so the report the agent reads before signing it said, literally:
 *
 *     Baths (full / half)    3 full / &mdash; half
 *     Pool                   &mdash;
 *
 * The deliverable was right and its on-screen twin was wrong, which is the worst way round: the
 * agent proof-reads the broken one. Same strings, same allow-listed pipeline, same trust basis as
 * `Html` above — the only difference is the element, so a value can sit inside a flex row. */
/* EXPORTED, so `OverrideTrail` can render the trail's sentences through THIS sink rather than
   growing a second one (2026-09-01, W2-UIFIX). `override_trail[].sentence` is composed by
   `engine/field_override.sentence_of` and carries `<b>` around both figures, exactly like every
   other prose field on this payload — it was the one that reached the screen escaped, so the
   review screen printed `recorded as <b>$612,000</b> by Dana Whitfield` as visible characters.
   The trust basis is unchanged and is stated once, above `Html`: `server/narrative_html.py`
   sanitises prose to `b i em strong br` where it ENTERS the payload. `security/serverEscaping.test.tsx`
   registers the sinks per FILE, and keeping both of them here is what keeps that register at two
   files and four sites — a component that needs server markup imports the sink, it does not
   reproduce it. */
export function HtmlSpan({ html, style, className }: { html: string; style?: React.CSSProperties; className?: string }) {
  return <span className={className} style={style} dangerouslySetInnerHTML={{ __html: inertHtml(html) }} />;
}

type DownloadFmt = "pdf" | "docx";
const FMT_LABEL: Record<DownloadFmt, string> = { pdf: "PDF", docx: "Word file" };

/** Start a download the agent asked for but whose click we had to swallow.
 *
 * Both download controls are real `<a href>` elements and stay that way — right-click, middle-
 * click and "save link as" keep working, and nothing about the page depends on JavaScript to get
 * a file. This is only for the one path where the click was intercepted to ask a question first
 * and then answered: the anchor is gone by then, so the navigation has to be re-issued. */
function startDownload(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * The build/report screen. As of Session 7, once the job is `done` this fetches the real report
 * data (`GET /api/next/amb/jobs/{id}/result` — `server/amb_next.py`, new this session) and renders
 * it inline, using the SAME data object `engine.amb_render` already turns into the PDF/DOCX (see
 * `cma/types.ts`'s `AmbReportData` docstring for exactly how that was grounded). The PDF/DOCX
 * download links stay — this is an ADDITIONAL view, not a replacement for the deliverable a
 * licensed professional actually signs and sends.
 *
 * If the result fetch fails for any reason, this degrades to Session 6's behavior (progress +
 * download links only) rather than showing a broken page — the download endpoints are the
 * long-standing, more heavily exercised path.
 */
export function NarrativeReportScreen({
  job,
  notice,
  error,
  stalled,
  onBackToReview,
  onStartOver,
  onRevoice,
  onSubjectPhoto,
  photoOptions,
  photoChoiceKey,
  onPhotoChoice,
  onPhotoRetry,
  builtPersonas,
  revoicing,
  revoiceError,
  revoiceBlocked,
  editedSinceBuild,
  revoiceWithheld,
  onRebuildWithEdits,
  view,
  onViewChange,
}: {
  job: AmbJobPublic | null;
  notice?: string | null;
  error?: string | null;
  /** UI-101: the poller has seen no change for 90 seconds. Rendered as an honest note, not as a
   * failure — the job may still land, and the page keeps checking either way. */
  stalled?: boolean;
  /** Only supplied when there is a review to go back TO (a rehydrated failed job has no grid). */
  onBackToReview?: () => void;
  onStartOver?: () => void;
  /** REBUILD THIS EXACT ANALYSIS IN ANOTHER VOICE (2026-08-07, owner request). Supplied only when
   * the flow is still holding the payload this report was built from — see `CmaAmbFlow`'s
   * `handleRevoice`. Absent, the block is not rendered: there is nothing the agent could do on this
   * screen to make it work, so a disabled control would be a puzzle rather than an affordance. */
  onRevoice?: (voice: PersonaId) => void;
  /** Told when the agent replaces the subject photo on this finished brief, so the host can carry it
   * into any later voice (2026-09-25). */
  onSubjectPhoto?: (dataUri: string) => void;
  /** The subject's photo options (MLS photos and Street View). Absent, the picker is not drawn: with
   * no job to ask about there is nothing the agent could pick. */
  photoOptions?: SubjectPhotoOptions | null;
  photoChoiceKey?: string | null;
  /** Told which option was saved on this brief, after `onSubjectPhoto` has carried its image. */
  onPhotoChoice?: (key: string | null) => void;
  onPhotoRetry?: () => void;
  /** The voices a file already exists for, so none of them is offered as if it were new. */
  builtPersonas?: PersonaId[];
  /** The voice currently being built, or null. The id, not a boolean, so the button that was
   * pressed is the one that says what it is doing. */
  revoicing?: PersonaId | null;
  revoiceError?: string | null;
  /** SET ONLY WHEN THE BROWSER REFUSED THE NEW TAB (2026-08-14). The job was still built and the
   * model call was still spent, so the id is carried here and offered as a link. A pop-up blocker
   * must not be able to turn a report the agent paid for into one they cannot reach. */
  revoiceBlocked?: { persona: PersonaId; jobId: string; href: string } | null;
  /** The report was reopened after a reload and the payload that built it was deliberately not
   * stored (it held MLS rows that are never saved). There is no re-voice to offer, so the control's
   * place says why. Supplied only when `onRevoice` is not. */
  revoiceWithheld?: boolean;
  /** THE AGENT HAS CHANGED SOMETHING SINCE THIS REPORT WAS BUILT (2026-08-07). Computed by
   * `CmaAmbFlow` as "would a build started now send different bytes than the one that made this?"
   * — see `editedSinceBuild` there. True, the voice buttons are not rendered at all: a re-voice
   * re-sends the stored payload unchanged by design, so offering one here would write a second file
   * from figures that have already been corrected and hand it over as current. */
  editedSinceBuild?: boolean;
  /** Where "generate again, with the change" goes. Omitted when there is no review to return to,
   * the same rule `onBackToReview` follows. */
  onRebuildWithEdits?: () => void;
  /** WHICH VIEW, WHEN THE HOST OWNS IT (S3-02). Supplied by `CmaAmbFlow`, which reads it from
   * `?view=` and writes it back, so the view is deep-linkable and the back button undoes a switch.
   * ABSENT, this screen keeps its own state and behaves exactly as it always has — which is why
   * fourteen existing tests that render it bare, with no router above them, still do.
   * Controlled and uncontrolled, and the uncontrolled half is not a courtesy: a screen that could
   * only be rendered inside a router would have made this slice a rewrite of its test suite. */
  view?: ReportView;
  onViewChange?: (next: ReportView) => void;
}) {
  const [result, setResult] = useState<AmbReportData | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);
  /** STEP 4's second half — "review & generate, THEN EDIT" (2026-08-01). Editing is a mode on this
   * screen rather than a separate route: the agent has just read the brief and wants to change a
   * sentence in it, and sending them somewhere else to do that loses the thing they were looking at. */
  const [editing, setEditing] = useState(false);
  /* TWO VIEWS OVER ONE REPORT  (2026-08-31, owner: "maybe there are 2 views so we dont break
     everything, the current view, and then full view").

     `working` is EXACTLY what this screen has always been — `ReportBody`, the layout picker, the
     downloads, the editor — unchanged, and it stays the default. Nothing an agent knows how to do
     today moves or behaves differently.

     `full` is the new one: the real PDF, page by page, with the editor beside it. It is opt-in and
     it is reversible in one click, which is the whole reason it is a switch rather than a
     replacement. A new view that cannot be left is a rewrite wearing a toggle. */
  const [ownView, setOwnView] = useState<ReportView>("working");
  /* The host's view if there is one, this screen's otherwise — and `resolveView` either way, so a
     `document` that arrived while the flag is off cannot become a state this screen is in. */
  const reportView: ReportView = resolveView(view ?? ownView);
  const inDocument = reportView === "document";
  /* PRESSING A VIEW BUTTON SHOULD TAKE YOU TO THE VIEW (2026-09-07).
     Measured at 1512: with the format picker, the photograph note, the Word caveat, both downloads
     and "Start over" above it, the document editor's toolbar began at y≈900 — below the fold on a
     950px window. An agent pressed "Edit on the page" and the page did not appear to change.
     Removing this screen's duplicate format picker takes ~200px off that; the rest is the file
     furniture, which legitimately belongs above. So the view brings itself into view.
     ONLY FOR THE DOCUMENT VIEW: the other two are read from the top of the page down and moving
     the scroll under an agent who is already reading would be the rudeness this fixes. */
  const switchRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!inDocument) return;
    const el = switchRef.current;
    // jsdom has no scrollIntoView; a missing method is a no-op, never a crash in a test run.
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
    /* `result` IS IN THE DEPS AND IS NOT DECORATION. The switch renders inside `{result && …}`, so
       on a direct load of `?view=document` this effect ran while the ref was still null and did
       nothing — measured: `window.scrollY` 0 with the toolbar at y≈690. Re-running when the result
       lands is what makes the ref exist by the time it is read. */
  }, [inDocument, result]);
  const setReportView = useCallback(
    (next: ReportView) => {
      if (onViewChange) onViewChange(next);
      else setOwnView(next);
    },
    [onViewChange],
  );
  /* Bumped after every accepted save so `ReportProof` re-renders the document. A counter rather
     than a boolean: two saves in a row must produce two rebuilds, and a boolean that is already
     true cannot say "again". */
  const [proofKey, setProofKey] = useState(0);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  /* ------------------------------------------------------------------------------------------
   * THE UNSAVED-EDIT TRAP (2026-08-10, from "my edits aren't landing when I generate the report").
   *
   * Nothing is sent until "Save the changes" is pressed, and the two download links sit ONE INCH
   * ABOVE the open editor. An agent who types a correction and reaches straight for Download PDF
   * used to get the pre-edit file, silently, with the corrected text still on screen underneath.
   * Nothing was broken; the page just never mentioned it.
   *
   * WHY INTERCEPT-AND-ASK RATHER THAN THE ALTERNATIVES:
   *   - Disabling the links while dirty would be a puzzle ("why is Download greyed out?") and
   *     would also remove a legitimate action: the last saved version is a real thing to want.
   *   - A permanent banner would be a nag; it would be on screen for the whole edit, including the
   *     ~100% of that time when nobody is about to download anything.
   *   - Auto-saving on download would send text the agent had not finished, through the server's
   *     figure gate, and could fail in the middle of a download they thought was routine.
   *   - A confirm dialog blocks the page and reads as an error.
   * So: while the editor is CLEAN nothing changes at all — the links are ordinary links and this
   * whole block is inert. The first click while DIRTY is swallowed and replaced with the choice
   * that was being made implicitly anyway, stated out loud and defaulting to the safe branch.
   * ------------------------------------------------------------------------------------------ */
  const [editorDirty, setEditorDirty] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<DownloadFmt | null>(null);
  const [savingForDownload, setSavingForDownload] = useState(false);
  /* THE CHOSEN LAYOUT, AND IT NOW OUTLIVES THE TAB (2026-09-21).
   *
   * THE DEFECT, in the owner's words: "panorama, downloads as a standard portrait view not
   * landscape." Every renderer was measured and every one was right. What was wrong is that this
   * state started at `null` on every mount, `downloadUrl` omits `&layout=` when it is null, and
   * the server then fell back to Focus — portrait. So the agent picked Panorama,
   * reloaded or came back from the briefs list, and downloaded a Focus brief without being told.
   * It also dropped their edits, because markup saved under "PAN" is invisible to a Focus render.
   *
   * SEEDED FROM THE JOB, WHICH NOW RECORDS IT. `null` still means "nobody chose one" and still
   * downloads what such a brief has always downloaded — it is not read as Focus. */
  const [layout, setLayout] = useState<string | null>(job?.layout ?? null);
  /* THE SEED IS AN EFFECT, NOT ONLY AN INITIALISER, because this screen mounts while the job is
     still polling and `job` arrives afterwards. It runs only while nothing has been chosen here,
     so it can never overwrite a choice the agent just made with the value the poll last saw. */
  useEffect(() => {
    const recorded = job?.layout ?? null;
    if (recorded !== null) setLayout((now) => (now === null ? recorded : now));
  }, [job?.layout]);

  /* RECORDED ON THE WAY OUT OF THE PICKER. Awaited rather than fired and forgotten: the downloads
     are plain `<a href>` links, so a click that lands before the write completes would download
     the previous layout. The local state moves first so the screen never feels like it is waiting
     on the network, and a failed write leaves the screen correct and the brief unrecorded —
     which is the state it was already in, not a new kind of wrong. */
  const chooseLayout = useCallback((next: string | null) => {
    setLayout(next);
    if (next === null || !job) return;
    void setJobLayout(job.id, next).catch(() => {
      /* absence-ok: the choice still governs this screen's downloads. Nothing is claimed about
         what a NEXT visit will show, and nothing is invented to cover it. */
    });
  }, [job]);
  // Populated BY the picker, not by a second fetch of the same endpoint -- see `onOptionsLoaded`.
  const [layoutOptions, setLayoutOptions] = useState<LayoutOption[] | null>(null);
  // Whether the CHOSEN layout can be produced as a Word file. Read from the registry entry, never
  // discovered by firing a request and handling the refusal: offering a button that cannot work
  // has already told the agent something untrue.
  const chosen = layoutOptions?.find((o) => o.code === layout) ?? null;
  const docxAvailable = chosen ? chosen.docx : true;

  /* WHICH LAYOUTS THIS BRIEF CANNOT PRODUCE (2026-08-15) — reported from production.
   *
   * The agent picked Folio on a brief built before the structured figure block, clicked Download,
   * and got a raw JSON page:
   *
   *   {"detail": "This brief was prepared before the structured figures were recorded, so it can
   *               only be produced in Focus. Regenerate it to use another layout."}
   *
   * The server's refusal is right — Folio needs the range low and high as separate values, Album
   * sizes each photograph by `weight / weight_max`, Panorama prints a $/sf row per column, and none
   * of that survives in Focus's rendered display strings. What was wrong is that the option was
   * offered at all. `LayoutPicker`'s contract is that it never offers a file that cannot be made.
   *
   * THIS IS A CAPABILITY CHECK, NOT A RE-IMPLEMENTED RULE. `figures` is the payload's OWN
   * declaration of what it carries — `figures.schema_version` is documented as the signal a
   * renderer checks — so reading its presence is reading the answer, not recomputing it. The
   * client does not, and must not, read any VALUE out of `figures`: ADR-0002, React is a pure
   * renderer. The server stays the authority; if it refuses anyway, its message still surfaces.
   *
   * Keyed by code rather than by a `landscape`/`docx`-style flag because the reason belongs to the
   * JOB, not to the layout: the same three codes are perfectly producible on the next brief. */
  const unavailableLayouts = (() => {
    if (!result) return undefined;              // nothing loaded yet — assume nothing is blocked
    const figs = result.figures;
    const reason =
      !figs
        ? "This brief was prepared before the structured figures were recorded, so it can only be produced in Focus. Generate a new report to use this layout."
        : !figs.comps || figs.comps.length === 0
          ? "This brief records no comparable sales in its figures, and this layout is made of them. Generate a new report to use it."
          : "";
    if (!reason) return undefined;
    // FCS is deliberately absent: Focus prints the display strings and never needs `figures`,
    // which is exactly why the server names it as the fallback in its own refusal.
    return { FOL: reason, PAN: reason, ALB: reason };
  })();
  const [guardError, setGuardError] = useState(false);
  const editorRef = useRef<ReportEditorHandle>(null);

  const guardDownload = (fmt: DownloadFmt) => (e: React.MouseEvent) => {
    if (!editorDirty) return; // clean: an ordinary link, and this never ran
    e.preventDefault();
    setGuardError(false);
    setPendingDownload(fmt);
  };

  function downloadNow(fmt: DownloadFmt) {
    if (!job) return;
    setPendingDownload(null);
    setGuardError(false);
    startDownload(downloadUrl(job.id, fmt, layout ?? undefined));
  }

  async function saveThenDownload() {
    const fmt = pendingDownload;
    if (!fmt) return;
    setSavingForDownload(true);
    setGuardError(false);
    try {
      // The editor's OWN save path, so a refusal is the server's refusal, shown by the editor,
      // naming the figure — not a second error message invented up here that says less.
      const saved = await editorRef.current?.save();
      if (saved) downloadNow(fmt);
      else setGuardError(true);
    } finally {
      setSavingForDownload(false);
    }
  }

  function closeEditor() {
    setEditing(false);
    setPendingDownload(null);
    setGuardError(false);
  }

  const loadResult = useCallback(() => {
    if (!job || job.status !== "done") return;
    getJobResult(job.id)
      .then((r) => setResult(r.data))
      .catch((e) => setResultError(e instanceof Error ? e.message : "Could not load the report inline."));
  }, [job?.status, job?.id]);

  useEffect(() => {
    if (!job || job.status !== "done") return;
    let cancelled = false;
    getJobResult(job.id)
      .then((r) => {
        if (!cancelled) setResult(r.data);
      })
      .catch((e) => {
        if (!cancelled) setResultError(e instanceof Error ? e.message : "Could not load the report inline.");
      });
    return () => {
      cancelled = true;
    };
  }, [job?.status, job?.id]);

  // UI-102: a null job used to paint a blank white page. Render a real state instead.
  if (!job) {
    return (
      <>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Starting your report</h1>
        {error ? (
          <div role="alert" style={{ marginTop: 20, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`, borderRadius: 10, padding: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: colors.caution }}>We couldn&apos;t start the build.</p>
            <p style={{ marginTop: 6, fontSize: 13, color: colors.textSecondary }}>{error}</p>
          </div>
        ) : (
          <p style={{ marginTop: 20, fontSize: 14, color: colors.textSecondary }}>Setting up the build…</p>
        )}
        <RecoveryActions onBackToReview={onBackToReview} onStartOver={onStartOver} />
      </>
    );
  }

  const isBlocked = job.status === "needs_input";
  const h1 =
    result ? result.report_title
      : job.status === "failed" ? "We hit a problem"
      : isBlocked ? "One more thing before we can build this"
      : "Building your report";

  return (
    <>
      <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{h1}</h1>

      {job.status !== "done" && job.status !== "failed" && !isBlocked && (
        <>
          {/* The real progress panel (2026-07-30): a percentage from the server's weighted
              per-job-type model, a bar, elapsed time, and an activity line keyed to the CURRENT
              stage so it never describes work that is not happening. Replaces a static card whose
              single label sat unchanged through the longest part of the build. */}
          <BuildProgress job={job} notice={notice} stalled={stalled} />
          {/* UI-011/UI-101: a dropped connection during the build used to stop the poll silently —
              the message existed but was written to a string only the intake screen renders. */}
          {error && (
            <p role="alert" style={{ marginTop: 12, fontSize: 13, color: colors.caution, lineHeight: 1.5 }}>
              {error}
            </p>
          )}
        </>
      )}

      {/* UI-100/UI-101: needs_input must never dead-end on a raw token. Show the reason and a way back. */}
      {isBlocked && (
        <div role="alert" style={{ marginTop: 20, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`, borderRadius: 10, padding: 20 }}>
          <p style={{ margin: 0, fontSize: 14 }}>{job.reason ?? "We need one more detail before this report can be built."}</p>
          <RecoveryActions onBackToReview={onBackToReview} onStartOver={onStartOver} primary />
        </div>
      )}

      {job.status === "failed" && (
        <div role="alert" style={{ marginTop: 20, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`, borderRadius: 10, padding: 20 }}>
          <p style={{ margin: 0, fontSize: 14, color: colors.caution }}>The build didn&apos;t complete.</p>
          <p style={{ marginTop: 6, fontSize: 13, color: colors.textSecondary }}>
            {job.reason ?? error ?? "The server didn't record a reason. Your review is still here — going back and pressing Generate again is the fastest next step."}
          </p>
          <RecoveryActions onBackToReview={onBackToReview} onStartOver={onStartOver} primary />
        </div>
      )}

      {job.status === "done" && (
        <>
          {/* NO LOCAL WIDTH CAP (2026-08-15). This was `maxWidth: 520` — a number picked before
              the page-measure system landed, and 60% of the 880px `prose` measure this screen
              actually uses. The result was a narrow column of format cards glued to the left of a
              page whose every other block — the heading, the estimate card, the voice panel — runs
              the full 880. It read as a different, smaller product bolted on.

              The measure belongs to the PAGE, not to this block: `AppShell content="prose"` already
              applies it via `pageMeasure`, so inheriting is what keeps the picker aligned with its
              neighbours at every viewport instead of only at the one where 520 happened to look
              deliberate. See ui/measures.ts. */}
          {/* ONE FORMAT PICKER PER SCREEN (2026-09-07). Photographed at 1512 on
              `/cma/job_demo?view=document`: this picker at the top of the page and the editor
              rail's own FORMAT group, four identical choices each, both live, both driving the same
              state. In the document view the rail's copy is the one beside the page it re-renders,
              so this one stands down — the same reasoning that keeps "Edit section by section"
              from appearing twice a hundred lines above.
              The `layout` STATE is untouched: the rail sets it, the downloads read it, and nothing
              about which format is selected depends on which control is drawn. */}
          {!inDocument && (
            <div style={{ marginTop: 16 }}>
              <LayoutPicker
                value={layout}
                onChange={chooseLayout}
                onOptionsLoaded={setLayoutOptions}
                unavailable={unavailableLayouts}
                note="Changing the format re-renders this brief from the analysis already run. It does not re-run the analysis and costs nothing."
              />
            </div>
          )}

          {/* WHAT THE PICTURES ARE. Renders nothing unless a photograph in this brief came from
              Street View, or a comparable has none — see PhotoSourceNote. It sits under the layout
              picker and above the download buttons on purpose: it is the last thing an agent reads
              before putting their name on the document. */}
          {result && !inDocument && <PhotoSourceNote data={result} />}

          {/* THE PHOTO THAT PRINTS, CHANGEABLE BEFORE DOWNLOAD (2026-09-25, owner P0). Saved to this
              brief on the server; every download re-renders from it, so no figure moves. */}
          {result && !inDocument && job && photoOptions !== undefined && (
            <SubjectPhotoPicker
              where="report"
              options={photoOptions}
              chosenKey={photoChoiceKey ?? null}
              uploadedInstead={result.hero?.subject_photo_source === "subject_upload" && !photoChoiceKey}
              onRetry={onPhotoRetry}
              onChoose={async (p) => {
                const uri = await photoOptionToDataUri(p);
                await setSubjectPhoto(job.id, uri);
                onSubjectPhoto?.(uri);
                onPhotoChoice?.(photoKey(p));
                loadResult();
                setProofKey((k) => k + 1);
              }}
            />
          )}
          {result && !inDocument && job && (
            <SubjectPhotoUpload
              where="report"
              photoOptionsShown={photoOptions?.kind === "ok" && photoOptions.photos.length > 0}
              photo={result.hero?.subject_photo ?? null}
              origin={result.hero?.subject_photo_source === "subject_upload"
                ? "your photo"
                : result.subject_photo_source === "streetview"
                  ? "a Google Street View image"
                  : "the photo read from your subject sheet"}
              onPhoto={async (uri) => {
                await setSubjectPhoto(job.id, uri);
                onSubjectPhoto?.(uri);
                loadResult();
                setProofKey((k) => k + 1);
              }}
            />
          )}

          <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {/* WHAT THE WORD FILE DOES NOT CARRY, SAID BEFORE THE DOWNLOAD AND NOT AFTER IT (owner,
              2026-09-06: the omission "must appear in the capability map and be named in the DOCX
              omissions UI in plain language before download starts").
              WIDENED 2026-09-06 from the crop to the whole edited document, because the crop was
              never the biggest half. `amb_report` reads the saved body at exactly one call site and
              only when the format is PDF (`tests/test_the_word_file_and_the_edited_body.py`), so a
              sentence rewritten in the document editor is in the PDF and not in Word -- and prose
              is what an agent is most likely to have changed. The FIGURES are identical either way:
              both files are rendered from the same frozen payload, and nothing here says a number
              differs.
              STATED WHENEVER WORD IS OFFERED rather than only when a crop exists: this screen does
              not read the saved document's assets, so a conditional sentence here would be a
              promise made from a place that cannot check it -- and a promise that is silently wrong
              is worse than a sentence that is sometimes unnecessary. Calm, sentence case, beside
              the control, not an alarm. */}
          {docxAvailable ? (
            <p style={{ marginTop: 12, marginBottom: 0, fontSize: 13, color: colors.textSecondary }}>
              The Word file is built from your edited document, the same as the PDF. Word has no
              page box and no grid, so leading, columns and background tints are approximate
              — the PDF is the typeset deliverable.
            </p>
          ) : null}

          {/* THE ACTIONS ARE THE PROTOTYPE'S BUTTONS NOW (2026-08-30), and the primary is NAVY.
                It was `colors.verified` — the green that means "verified against a named source"
                everywhere else in this product — so the loudest control on the report screen was
                painted in a provenance colour. `.btn.p` is the prototype's primary; `.btn` its
                secondary. Word was a bare bordered link beside a filled green button, which is
                why it read as missing: it is now the same control as its neighbour, one step
                quieter. */}
            <a href={downloadUrl(job.id, "pdf", layout ?? undefined)} onClick={guardDownload("pdf")} className="btn p" style={{ textDecoration: "none" }}>
              Download PDF
            </a>
            {docxAvailable ? (
              <a href={downloadUrl(job.id, "docx", layout ?? undefined)} onClick={guardDownload("docx")} className="btn" style={{ textDecoration: "none" }}>
                Download Word
              </a>
            ) : (
              /* SAID, NOT HIDDEN. A button that vanishes reads as a bug; a sentence explains the
                 trade the agent just made and how to undo it. */
              <span style={{ padding: "9px 16px", fontSize: 13, color: colors.textSecondary }}>
                {chosen?.name} is a PDF only. Choose Focus above for a Word file.
              </span>
            )}
            {/* EDIT IS OFFERED ONLY WHEN THE REPORT ACTUALLY LOADED. Nothing is a stored artifact —
                both downloads re-render from the same persisted dict this edits — so an edit made
                against a preview that failed to load would be an edit against something the agent
                never saw. */}
            {/* ONE OF THESE PER SCREEN, NEVER TWO  (2026-09-01, W2-UIFIX, from
                `w1-harness/report-full@1512.png`, where "Edit the wording" appeared twice on one
                screen). The full-report view hands `ReportProof` its own copy of this control,
                in the toolbar directly above the pages it edits — which is the better place for
                it there, because in that view the page head can be a screen and a half above the
                sheet the agent is reading. So this one stands down in `full` and is the only one
                in `working`. Two buttons with one label and one behaviour is a reader working out
                whether they differ; they never did. */}
            {result && !editing && reportView === "working" && (
              <button
                type="button"
                onClick={() => { setEditing(true); setSavedNote(null); }}
                className="btn"
                style={{ borderColor: colors.accent, background: colors.accentSoft, color: colors.accent }}
                title="Opens the brief's sections as fields you fill in, one at a time"
              >
                Edit section by section
              </button>
            )}
          </div>

          {/* The intercepted click, answered. Sits immediately under the two links so the question
              is where the press was, and it names the format that was actually clicked. */}
          {pendingDownload && (
            <div
              role="alert"
              style={{ marginTop: 12, padding: "12px 14px", borderRadius: 10, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}` }}
            >
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
                <strong>Your edits haven&apos;t been saved yet.</strong> The {FMT_LABEL[pendingDownload]} is
                re-rendered from the saved report, so downloading it right now would give you the
                wording as it was before you started typing.
              </p>
              <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <button className="vb-btn"
                  type="button"
                  onClick={saveThenDownload}
                  disabled={savingForDownload}
                  style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: savingForDownload ? colors.neutralChip : colors.verified, color: savingForDownload ? colors.textSecondary : "#fff", fontWeight: 600, fontSize: 13, cursor: savingForDownload ? "not-allowed" : "pointer" }}
                >
                  {savingForDownload ? "Saving…" : `Save my edits, then download the ${FMT_LABEL[pendingDownload]}`}
                </button>
                {/* Kept, deliberately. The last saved version is a legitimate thing to want, and
                    the point of this panel is that taking it is now a stated choice rather than
                    something that happened to you. */}
                <button
                  type="button"
                  onClick={() => downloadNow(pendingDownload)}
                  disabled={savingForDownload}
                  style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: savingForDownload ? "not-allowed" : "pointer" }}
                >
                  Download without my edits
                </button>
                <button
                  type="button"
                  onClick={() => { setPendingDownload(null); setGuardError(false); }}
                  disabled={savingForDownload}
                  style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: savingForDownload ? "not-allowed" : "pointer" }}
                >
                  Keep editing
                </button>
              </div>
              {guardError && (
                <p style={{ margin: "10px 0 0", fontSize: 13, color: colors.caution, lineHeight: 1.5 }}>
                  The save didn&apos;t go through, so nothing was downloaded. The reason is on the
                  editor below, and every word you typed is still in it.
                </p>
              )}
            </div>
          )}

          {savedNote && (
            <p role="status" style={{ marginTop: 12, fontSize: 13, color: colors.verified }}>
              {savedNote}
            </p>
          )}

          {/* A FINISHED REPORT IS NOT A DEAD END EITHER (2026-08-07, owner request: "the ability to
              go back to any of the steps in the workflow ... without rerunning the entire
              workflow").
              `RecoveryActions` was rendered on `needs_input`, on `failed` and on a null job — every
              terminal state EXCEPT the one an agent actually reaches, which is a report that
              worked. So the exits existed precisely when the run had gone wrong, and the successful
              path offered a download and nothing else: an agent who wanted to swap one comparable
              had no way back short of starting again with the file. Same two exits, same
              component, now on the state that matters most. */}
          <RecoveryActions onBackToReview={onBackToReview} onStartOver={onStartOver} />

          {onRevoice && (
            <RevoiceControl
              onRevoice={onRevoice}
              builtPersonas={builtPersonas ?? []}
              revoicing={revoicing ?? null}
              error={revoiceError ?? null}
              blocked={revoiceBlocked ?? null}
              editedSinceBuild={!!editedSinceBuild}
              onRebuildWithEdits={onRebuildWithEdits}
            />
          )}

          {!onRevoice && revoiceWithheld && (
            <section
              aria-labelledby="vb-revoice"
              style={{
                marginTop: 22, padding: "16px 18px", borderRadius: 10,
                background: colors.bgSurface, border: `1px solid ${colors.border}`,
              }}
            >
              <h2 id="vb-revoice" style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                The same brief, in another voice
              </h2>
              <p role="status" style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.6, color: colors.textSecondary }}>
                Another voice isn&apos;t available after a reload because this report used MLS rows that
                aren&apos;t saved. Go back to Review, then Generate to use it.
              </p>
            </section>
          )}

          {!result && !resultError && <p style={{ marginTop: 16, fontSize: 13, color: colors.textSecondary }}>Loading the report…</p>}
          {resultError && (
            <p style={{ marginTop: 16, fontSize: 13, color: colors.textSecondary }}>
              Couldn&apos;t load an inline preview ({resultError}) — the PDF/DOCX above is the deliverable either way.
            </p>
          )}

          {/* THE VIEW SWITCH. Placed here, immediately above the thing it switches, rather than up
              with the downloads: the controls above act on the FILE (which format, which layout,
              download it), and this one acts on what is shown on screen. Two different questions
              should not sit in one row pretending to be the same kind of choice. */}
          {result && (
            <div ref={switchRef}
                 style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                          flexWrap: "wrap", gap: 12, margin: "24px 0 12px",
                          paddingBottom: 10, borderBottom: `1px solid ${colors.border}`,
                          scrollMarginTop: 12 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>
                {reportView === "full"
                  ? "The report, page by page"
                  : reportView === "document"
                    ? "The report, as a document you can edit"
                    : "The report"}
              </h2>
              <div className="vb-view-switch" role="group" aria-label="How to show the report">
                <button type="button" aria-pressed={reportView === "working"}
                        onClick={() => setReportView("working")}>Working view</button>
                <button type="button" aria-pressed={reportView === "full"}
                        onClick={() => setReportView("full")}>Full report</button>
                {/* THE THIRD SIDE OF A SWITCH THAT ALREADY EXISTED, rather than a link somewhere
                    else. The agent has just read the brief; "edit the document" is a way of
                    looking at the same brief, not a departure from it. Not rendered at all while
                    the flag is off — a disabled control is a puzzle, and `resolveView` has already
                    made the state unreachable, so this is the affordance and not the guard. */}
                {DOCUMENT_EDITOR_ENABLED && (
                  <button type="button" aria-pressed={reportView === "document"}
                          onClick={() => setReportView("document")}>Edit on the page</button>
                )}
              </div>
            </div>
          )}

          {/* WHAT EACH VIEW IS FOR, in one sentence, because "working" and "full" are not
              self-explanatory and a switch whose two sides look interchangeable gets clicked once
              and then ignored.
              ALL THREE NOW, NOT ONLY `full` (2026-09-07). Walked in a browser: the switch offers
              three ways of looking at one brief and, beside them, a fourth control that also says
              "edit" — and only one of the four said what it was for. An agent meeting "Working
              view · Full report · Edit on the page" has no way to tell which one to press, and
              the two that carry the word "edit" are genuinely different tools. One line each. */}
          {/* THE DOCUMENT VIEW SAYS IT ONE LINE LOWER, AND SAYING IT TWICE COST 49px (2026-09-14).
              Walked at 1280x950: this paragraph, the editor's own summary line and the rail's
              "How this works" block were three copies of the same guidance stacked above a
              document that then began halfway down the window. The one-line-per-view rule is kept
              — the editor's `<summary>` is that line for this view, it is always visible whether
              the explanation is folded or not, and it sits closer to the thing it describes. */}
          {result && reportView !== "document" && (
            <p style={{ margin: "0 0 14px", fontSize: 12.5, lineHeight: 1.55,
                        color: colors.textSecondary, maxWidth: measure.copy }}>
              {reportView === "full"
                ? "These are the actual pages the Download PDF button produces, in the layout selected above — not a second drawing of them. Use this to check the cover, the page breaks and anything that has to fit on one sheet."
                : "Everything the analysis produced, laid out to be checked rather than printed. Every figure carries the source it came from, and this is where the file downloads live."}
            </p>
          )}

          {result && reportView === "full" ? (
            <div className="vb-proof-shell">
              <div className={editing ? "vb-proof-workspace" : undefined}>
                <ReportProof
                  jobId={job.id}
                  layout={layout ?? undefined}
                  reloadKey={proofKey}
                  actions={!editing && (
                    <button
                      type="button"
                      onClick={() => { setEditing(true); setSavedNote(null); }}
                      className="btn"
                      style={{ borderColor: colors.accent, background: colors.accentSoft,
                               color: colors.accent, padding: "5px 12px", fontSize: 12.5 }}
                      title="Opens the brief's sections as fields you fill in, one at a time"
                    >
                      Edit section by section
                    </button>
                  )}
                />
                {/* THE EDITOR SITS BESIDE THE PAGES, WHICH IS THE POINT OF THIS VIEW. Correcting a
                    sentence and seeing where it lands used to be two screens and a download apart.
                    Same component, same handle, same save path — only its position changed, so
                    every guard the working view has (the dirty trap, the figure gate, the server's
                    own refusal text) applies here unchanged. */}
                {editing && (
                  <ReportEditor
                    ref={editorRef}
                    jobId={job.id}
                    onDirtyChange={setEditorDirty}
                    onSaved={() => {
                      closeEditor();
                      loadResult();
                      // The pages on screen were rendered before this save, so they are now stale.
                      // Re-rendering them is the difference between a preview and a screenshot.
                      setProofKey((k) => k + 1);
                      setSavedNote("Saved. These pages have been rebuilt with your words.");
                    }}
                    onCancel={closeEditor}
                  />
                )}
              </div>
            </div>
          ) : reportView === "document" ? (
            /* NOT GATED ON `result`, unlike the two views above it, and deliberately. `result` is
               the INLINE PREVIEW's fetch; the document view has its own, of a different document,
               from a different endpoint. Making the editor wait on an unrelated request means a
               brief whose preview failed cannot be edited — and the preview failing is a state
               this screen already renders a sentence about. Two fetches, two failures, two
               messages. */
            /* THE DOCUMENT EDITOR, IN PLACE. Same screen, same URL, same job — the panel takes the
               id and `job.ts` decides whether there is a document to draw, exactly as it does on
               the standalone route. The boundary is a Suspense because the chunk is lazy; there is
               no error boundary here because `DocumentEditorPanel` has no throwing state — it
               renders its own refusal, in words, which is a better failure than a boundary's. */
            /* THE HEIGHT IS RESERVED BEFORE THE CHUNK ARRIVES, AND THAT IS LOAD-BEARING
               (2026-09-07). Instrumented in a real browser: the effect above fires twice, and on
               the second run — 18ms later, with the ref attached — `scrollIntoView` did nothing,
               because at that instant the ProseMirror chunk had not mounted, the document was
               950px tall in a 950px window, and there was nowhere to scroll TO. The page then grew
               to 4752px and the scroll stayed at 0. A race, and one that would have been slower and
               more reliable on the owner's machine than on this one.
               Reserving the editor's space makes the page scrollable in the same commit that
               renders the switch, so the scroll has somewhere to go. It also stops the screen
               jumping when the chunk lands, which is worth having on its own. */
            <div style={{ minHeight: "88vh" }}>
              <Suspense fallback={<p className="vb-ed-screen-note">Loading the editor…</p>}>
                <DocumentEditorPanel jobId={job.id} />
              </Suspense>
            </div>
          ) : editing ? (
            <ReportEditor
              ref={editorRef}
              jobId={job.id}
              onDirtyChange={setEditorDirty}
              onSaved={() => {
                closeEditor();
                // Re-fetch rather than patching local state: the server is the only thing that
                // knows what it actually stored after stripping and re-escaping, and the preview
                // has to show the same text the PDF will print.
                loadResult();
                setProofKey((k) => k + 1);
                setSavedNote("Saved. The next PDF or Word download carries these words.");
              }}
              onCancel={closeEditor}
            />
          ) : (
            result && <ReportBody data={result} />
          )}
        </>
      )}
    </>
  );
}

/**
 * UI-101 / NAVIGATION rule 6 — "no dead-ends." Every terminal state on this screen offers the same
 * two exits: back to the review that is still in memory, and a clean restart. `Back to review` is
 * omitted (not disabled) when there is no review to return to — a rehydrated failed job has no
 * grid, and a button that goes nowhere is worse than no button.
 */
function RecoveryActions({
  onBackToReview,
  onStartOver,
  primary,
}: {
  onBackToReview?: () => void;
  onStartOver?: () => void;
  primary?: boolean;
}) {
  if (!onBackToReview && !onStartOver) return null;
  return (
    <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
      {onBackToReview && (
        <button className="vb-btn"
          type="button"
          onClick={onBackToReview}
          style={{
            padding: "9px 16px",
            borderRadius: 8,
            border: primary ? "none" : `1px solid ${colors.border}`,
            background: primary ? colors.accent : colors.bgSurface,
            color: primary ? "#fff" : colors.textPrimary,
            fontWeight: primary ? 600 : 400,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Back to review
        </button>
      )}
      {onStartOver && (
        <button className="vb-btn"
          type="button"
          onClick={onStartOver}
          style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: "pointer" }}
        >
          Start over with a different file
        </button>
      )}
    </div>
  );
}

/**
 * THE SAME FIGURES, ANOTHER VOICE (2026-08-07, owner request: "the ability to go back to any of the
 * steps in the workflow to regenerate the file with a different persona or make adjustments without
 * rerunning the entire workflow. we need to allow this to be so much easier than just rerunning the
 * entire process from beginning to end").
 *
 * It sits beside "Edit the wording" on purpose: both are "the brief is right and I want it to read
 * differently", and they are the two ways of doing that. Editing changes these words; this writes
 * new ones in a different register from the same evidence.
 *
 * WHAT THE COPY IS ALLOWED TO PROMISE. "The figures cannot change" is not reassurance, it is a
 * property of how the rebuild is sent: `CmaAmbFlow` re-posts the payload this report was built from,
 * unchanged, with `meta.persona` as the only difference — the comparables, the subject, the tier
 * and the agent's own value adjustments are the same bytes. The build then opens at `compute+draft`
 * and never re-reads a file, which is why this costs one model call rather than the whole workflow.
 *
 * A voice a file already exists for is shown as already built and cannot be pressed — a second
 * identical build would spend a model call to produce a duplicate.
 */
function RevoiceControl({
  onRevoice,
  builtPersonas,
  revoicing,
  error,
  blocked,
  editedSinceBuild,
  onRebuildWithEdits,
}: {
  onRevoice: (voice: PersonaId) => void;
  builtPersonas: PersonaId[];
  revoicing: PersonaId | null;
  blocked?: { persona: PersonaId; jobId: string; href: string } | null;
  error: string | null;
  /** See the prop of the same name on `NarrativeReportScreen`. */
  editedSinceBuild: boolean;
  onRebuildWithEdits?: () => void;
}) {
  const built = new Set(builtPersonas);
  /* AN EDIT SINCE THE BUILD REPLACES THIS CONTROL RATHER THAN DISABLING IT (2026-08-07).
   *
   * Everything the paragraph below promises — "the estimate, the range and the grid cannot move" —
   * is true because the payload is re-sent unchanged, and stops being true as a description of what
   * the agent wants the moment they have corrected something. Greying five buttons under a
   * paragraph that still says the figures are locked would be the same claim, quieter. The section
   * keeps its heading so the agent's eye lands where they were looking, and says the one thing
   * there is to say. */
  if (editedSinceBuild) {
    return (
      <section
        aria-labelledby="vb-revoice"
        style={{
          marginTop: 22, padding: "16px 18px", borderRadius: 10,
          background: colors.bgSurface, border: `1px solid ${colors.border}`,
        }}
      >
        <h2 id="vb-revoice" style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
          The same brief, in another voice
        </h2>
        <StaleReportNotice onRebuild={onRebuildWithEdits} />
      </section>
    );
  }
  return (
    <section
      aria-labelledby="vb-revoice"
      style={{
        marginTop: 22, padding: "16px 18px", borderRadius: 10,
        background: colors.bgSurface, border: `1px solid ${colors.border}`,
      }}
    >
      <h2 id="vb-revoice" style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
        The same brief, in another voice
      </h2>
      {/* THE LAST SENTENCE IS THE PROMISE THIS CONTROL NOW KEEPS (2026-08-14, owner request).
          It said "You keep this report" while the click swapped the screen for the download set
          and took any narrative edits with it — the copy was describing the behaviour everyone
          wanted rather than the behaviour it had. It opens in its own tab now, and says so, so an
          agent knows before they click that nothing here is about to move. */}
      <p style={{ margin: "6px 0 0", fontSize: 13, color: colors.textSecondary, lineHeight: 1.55 }}>
        This rebuilds the report you are looking at from the figures you already confirmed — the
        comparables, the subject and anything you added are sent again exactly as they were, and the
        voice is the only thing that differs. Nothing is read or re-read, so the estimate, the range
        and the grid cannot move. The new voice <strong>opens in a new tab</strong>: this report
        stays exactly as it is, and any wording you are part-way through editing stays with it.
      </p>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        {PERSONAS.map((p) => {
          const already = built.has(p.id);
          const busy = revoicing === p.id;
          const disabled = already || revoicing !== null;
          return (
            <button
              key={p.id}
              type="button"
              disabled={disabled}
              onClick={() => onRevoice(p.id)}
              title={already ? "You already have this voice" : p.descriptor}
              style={{
                padding: "7px 12px",
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                background: colors.bgApp,
                color: disabled ? colors.textSecondary : colors.textPrimary,
                fontSize: 13,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled && !busy ? 0.6 : 1,
              }}
            >
              {/* The state is in the words, not only in the colour — the same rule the step rail
                  follows, and the reason a greyed button here would not be enough. */}
              {busy ? `${p.name} — writing…` : already ? `${p.name} — already built` : p.name}
            </button>
          );
        })}
      </div>
      {blocked && (
        // THE POP-UP WAS BLOCKED AND THE REPORT EXISTS ANYWAY. Silence here would leave the agent
        // having spent a model call on a brief with nothing on screen pointing at it. `target`
        // is set so following the link still cannot cost them this tab.
        <p
          role="status"
          style={{
            margin: "10px 0 0", padding: "10px 12px", borderRadius: 8,
            background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`,
            fontSize: 13, lineHeight: 1.55, color: colors.textPrimary,
          }}
        >
          Your browser blocked the new tab, but the report was written.{" "}
          <a href={blocked.href} target="_blank" rel="noreferrer" style={{ color: colors.accent, fontWeight: 600 }}>
            Open it in a new tab
          </a>{" "}
          — or allow pop-ups for this site and the next one will open on its own. Job {blocked.jobId}.
        </p>
      )}
      {error && (
        <p role="alert" style={{ margin: "10px 0 0", fontSize: 13, color: colors.caution, lineHeight: 1.5 }}>
          {error}
        </p>
      )}
    </section>
  );
}

function ReportBody({ data }: { data: AmbReportData }) {
  return (
    <div style={{ marginTop: 24 }}>
      {/* Hero verdict — every figure here is the engine's own output, never re-derived by this UI. */}
      {/* THE VERDICT IS NOT AN EVIDENCE CLAIM (2026-08-30). This was a `verifiedBg` panel with a
          `verifiedLine` border — the VERIFIED tier's own two colours — so the headline figure was
          framed in the palette that elsewhere means "confirmed against a named source". The
          prototype's conclusion is a white card with a hairline and the figure at 30px; the
          provenance belongs on the figure's own chip, not on the box around it.
          `.mono` rather than the generic `monospace` keyword, which resolved to Menlo/Courier here
          while the CRM's figures rendered in JetBrains Mono — the same number in two typefaces. */}
      <section style={{ background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 20 }}>
        <p className="vb-sec-label">
          Estimated
        </p>
        <p className="mono tnum" style={{ margin: "4px 0 0", fontSize: 30, fontWeight: 700, letterSpacing: "-0.02em" }}>{data.hero.estimated}</p>
        <Html html={`Range ${data.hero.range}`} style={{ marginTop: 4, fontSize: 14, color: colors.textSecondary }} />
        <p style={{ marginTop: 4, fontSize: 13, color: colors.textSecondary }}>Suggested list: {data.hero.list}</p>
        <Html html={data.hero.range_note} style={{ marginTop: 8, fontSize: 12, color: colors.textSecondary }} />
      </section>

      {/* THE SEPARATOR IS PRINTED BETWEEN PARTS THAT EXIST (2026-08-04). Written with two literal
          "·" characters, a subject with no subdivision — which is most of them, since the field is
          optional everywhere — rendered as "4815 Springtree Lane, Wylie, TX 75098 · ·": a dangling
          pair of dots under the headline figure. This is the SAME defect `amb_build` fixed on the
          PDF/DOCX side by building the string once instead of concatenating it twice; the React
          screen still concatenated. Filter, then join. */}
      <p style={{ marginTop: 12, fontSize: 12, color: colors.textSecondary }}>
        {[data.property.address_line, data.property.city_state_zip, data.property.subdivision]
          .map((s) => (typeof s === "string" ? s.trim() : ""))
          .filter(Boolean)
          .join(" · ")}
      </p>

      <Html html={data.lead} style={{ marginTop: 20, fontSize: 14, lineHeight: 1.6 }} />

      <section style={{ marginTop: 20 }}>
        <h2 style={{ fontSize: 15 }}>About this home</h2>
        <Html html={data.about} style={{ fontSize: 14, lineHeight: 1.6, marginTop: 6 }} />
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {data.your_home.map(([label, value]) => {
            /* THE TAG THE PDF AND THE WORD FILE ALREADY PRINT ON THIS ROW, AND THIS SCREEN DROPPED.
             *
             * `amb_build._your_home_sources` builds a {row label -> source} map and puts it on the
             * payload as `property.your_home_src`; `amb_render._facts` and `_dfacts` print it as a
             * small muted tag beside the value in both deliverables. This screen printed the value
             * alone, so the on-screen twin of the brief was the one WITHOUT the provenance — the
             * same shape of defect as the em-dash entities `HtmlSpan` exists to fix, and the worse
             * way round again, because the agent proof-reads the screen.
             *
             * It is a LOOKUP BY THE SERVER'S OWN KEY, which is exactly what both renderers do; a
             * row the map does not mention simply shows no tag, and nothing here decides what a
             * missing tag would have said.
             *
             * WHY THIS ROW CANNOT OPEN THE SOURCE DRAWER. The drawer renders a `Field`, and a
             * `Field` must carry a tier. `your_home_src` carries a source and no tier — the map's
             * values are "You entered", "Public record" or "Provided" — and turning one of those
             * into "verified" or "assumption" would be this client classifying a valuation input.
             * ADR-0002: React renders, it does not decide. So the row states its source and stops
             * there; see `AreaFactsSection` for the one block of this payload that does state a
             * tier, and the change note for what is missing everywhere else. */
            const srcMap = data.property?.your_home_src;
            const rawTag = srcMap ? srcMap[label] : undefined;
            const tag = typeof rawTag === "string" ? rawTag.trim() : "";
            return (
              /* `.kv` — the prototype's label-left / value-right row with a hairline between.
                 Same markup this already had; the port supplies the type ramp and the rule. */
              <div key={label} className="kv">
                <HtmlSpan html={label} style={{ color: colors.textSecondary }} />
                <span>
                  <HtmlSpan html={value} className="mono tnum" />
                  {tag && (
                    <span style={{ fontSize: 11.5, color: colors.textSecondary, marginLeft: 6 }}>
                      · {tag}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHAT THE AGENT OVERRODE, AND WHY (2026-08-15).
          Immediately above "What you added" because the two are the same kind of thing seen from
          opposite ends: `value_adjustments` is a figure the agent entered that MOVED the estimate,
          and the override trail is a figure they recorded that deliberately did not. Reading them
          together is how an agent checks that the document says what they meant it to. Renders
          nothing when nothing was overridden, which is nearly every brief — see OverrideTrail. */}
      <OverrideTrail data={data} />

      {/* WHAT THE AGENT CHANGED, AND BY HOW MUCH (2026-08-04).
          Every figure here is the ENGINE'S — `manual_value_adjustments` produced the label and the
          signed amount, and `amb_build` put them on the payload. Nothing is recomputed in the
          browser, which is the whole point: the input screen two steps back can only DESCRIBE what
          an entry means, because the pool baseline slides with the reconciled value and the value
          does not exist until the build has run. This is where it exists. */}
      {data.value_adjustments && data.value_adjustments.lines.length > 0 && (
        <section style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: 15 }}>What you added</h2>
          <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 6, lineHeight: 1.55 }}>
            The comparable sales alone reconcile to {data.value_adjustments.reconciled}. Each line
            below is a figure you entered, applied after that reconciliation and attributed to you
            in the report. The comparable grid itself is untouched by any of them.
          </p>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 13, display: "flex", justifyContent: "space-between", borderBottom: `1px solid ${colors.border}`, padding: "4px 0" }}>
              <span style={{ color: colors.textSecondary }}>From the comparable sales</span>
              <span className="mono tnum">{data.value_adjustments.reconciled}</span>
            </div>
            {data.value_adjustments.lines.map((ln, i) => (
              <div key={i} style={{ fontSize: 13, display: "flex", justifyContent: "space-between", borderBottom: `1px solid ${colors.border}`, padding: "4px 0" }}>
                <HtmlSpan html={ln.label} style={{ color: colors.textSecondary }} />
                <HtmlSpan html={ln.amount} className="mono tnum" />
              </div>
            ))}
            <div style={{ fontSize: 13, display: "flex", justifyContent: "space-between", padding: "6px 0", fontWeight: 600 }}>
              <span>Estimate as reported</span>
              <span className="mono tnum">{data.value_adjustments.final}</span>
            </div>
          </div>
        </section>
      )}

      <section style={{ marginTop: 20 }}>
        <h2 style={{ fontSize: 15 }}>{data.neigh_title}</h2>
        {data.neighborhood.map((p, i) => (
          <Html key={i} html={p} style={{ fontSize: 14, lineHeight: 1.6, marginTop: i === 0 ? 6 : 10 }} />
        ))}
      </section>

      {data.cards.length > 0 && (
        <section style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: 15 }}>Comparable sales</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            {data.cards.map((c, i) => (
              <div key={i} style={{ background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <strong style={{ fontSize: 14 }}>{c.name}</strong>
                  <span className="mono tnum" style={{ fontSize: 14 }}>{c.adj}</span>
                </div>
                <Html html={c.sub} style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }} />
                <p style={{ fontSize: 12, color: colors.accent, marginTop: 4 }}>{c.tag}</p>
                <Html html={c.blurb} style={{ fontSize: 13, lineHeight: 1.5, marginTop: 6 }} />
                {/* THE PER-SALE DISCLOSURE. `flag_reason` is a finished sentence the SERVER wrote
                    (server/amb_build.py, through engine/format_pairing.flag_of — the same string
                    the PDF layouts and their Word twins print). ADR-0002: this component decides
                    nothing about it; it renders the words or, when there are none, nothing at all.

                    Plain text, not `Html`: the string is composed server-side from engine values
                    and carries no markup, so it goes through React's escaping rather than the
                    raw-HTML sink the pipeline prose fields use.

                    Styled as a NOTICE, never as an error. A flagged sale is still in the
                    valuation at its full weight — this is disclosure, not exclusion — so there is
                    no alarm background and no `role="alert"`. The word "Disclosed" leads because
                    colour must never carry the meaning on its own (design-tokens.ts). */}
                {c.flag_reason ? (
                  <p style={{ fontSize: 12, lineHeight: 1.5, marginTop: 8, paddingLeft: 8, borderLeft: `2px solid ${colors.caution}`, color: colors.caution }}>
                    <strong>Disclosed</strong> {c.flag_reason}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={{ marginTop: 20 }}>
        <h2 style={{ fontSize: 15 }}>What is sitting</h2>
        <Html html={data.sitting} style={{ fontSize: 14, lineHeight: 1.6, marginTop: 6 }} />
      </section>

      <section style={{ marginTop: 20 }}>
        <h2 style={{ fontSize: 15 }}>How I reached this figure</h2>
        <Html html={data.how_reached} style={{ fontSize: 14, lineHeight: 1.6, marginTop: 6 }} />
      </section>

      <section style={{ marginTop: 20 }}>
        <h2 style={{ fontSize: 15 }}>Pricing</h2>
        <div style={{ display: "flex", gap: 18, marginTop: 6, fontSize: 13 }}>
          <span>List: <strong className="mono tnum">{data.pricing.list}</strong></span>
          <span>Expect: <strong className="mono tnum">{data.pricing.expect}</strong></span>
          <span>~30-day: <strong className="mono tnum">{data.pricing.fast}</strong></span>
        </div>
        <Html html={data.pricing.rationale} style={{ fontSize: 14, lineHeight: 1.6, marginTop: 8 }} />
      </section>

      {data.note.length > 0 && (
        <section style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: 15 }}>Agent note</h2>
          {/* S-1 (P1-1): note paragraphs can include VERBATIM user-authored bio text — render as
              plain text (React escapes), never through the raw-HTML sink used by pipeline fields. */}
          {data.note.map((p, i) => (
            <p key={i} style={{ fontSize: 14, lineHeight: 1.6, marginTop: i === 0 ? 6 : 10 }}>{p}</p>
          ))}
        </section>
      )}

      {/* Disclosures — small, sentence-case, never alarm-styled, per the project's own convention. */}
      <section style={{ marginTop: 28, paddingTop: 14, borderTop: `1px solid ${colors.border}` }}>
        <Html html={data.notappr} style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.6 }} />
        <Html html={data.limits} style={{ fontSize: 12, color: colors.textSecondary, marginTop: 10, lineHeight: 1.6 }} />
        <Html html={data.disclaimer_535} style={{ fontSize: 12, color: colors.textSecondary, marginTop: 10, lineHeight: 1.6 }} />
      </section>

      {/* THE ONE PLACE ON THIS PAYLOAD WHERE A READER CAN OPEN THE SOURCE BEHIND A FIGURE.
          Renders nothing unless the area-facts provider is on AND a human confirmed at least one
          fact — the same omission rule both deliverable renderers follow. It sits after the
          disclosures because that is where `engine/amb_render` splices it into the document, and
          the screen and the PDF should not order the same brief two different ways. */}
      <AreaFactsSection data={data} />
    </div>
  );
}
